import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { contactContent } from "../../content/marketing/contact";

type FieldErrors = {
  name?: string;
  email?: string;
  message?: string;
};

type Status = "idle" | "submitting" | "success" | "error";

const MAX_NAME_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 4000;
const MAX_EMAIL_LENGTH = 320;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(name: string, email: string, message: string): FieldErrors {
  const errors: FieldErrors = {};
  if (name.trim().length === 0) {
    errors.name = "Name is required.";
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.name = `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;
  }
  if (email.trim().length === 0) {
    errors.email = "Email is required.";
  } else if (email.length > MAX_EMAIL_LENGTH) {
    errors.email = `Email must be ${MAX_EMAIL_LENGTH} characters or fewer.`;
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = "Enter a valid email address.";
  }
  if (message.trim().length === 0) {
    errors.message = "Message is required.";
  } else if (message.length > MAX_MESSAGE_LENGTH) {
    errors.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`;
  }
  return errors;
}

export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formRenderedAt] = useState(() => Date.now());

  if (status === "success") {
    return (
      <output className="block text-base">
        {contactContent.successMessage}
      </output>
    );
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors = validate(name, email, message);
    setFieldErrors(errors);
    setErrorMessage(null);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          website,
          timeSpentMs: Date.now() - formRenderedAt,
        }),
      });

      if (res.ok) {
        setStatus("success");
        return;
      }
      if (res.status === 400) {
        const body = (await res.json()) as {
          errors?: Record<string, string[]>;
        } | null;
        const nextFieldErrors = {
          name: body?.errors?.name?.[0],
          email: body?.errors?.email?.[0],
          message: body?.errors?.message?.[0],
        };
        setFieldErrors(nextFieldErrors);
        if (!Object.values(nextFieldErrors).some(Boolean)) {
          setErrorMessage(contactContent.errorMessages.server);
        }
        setStatus("error");
        return;
      }
      if (res.status === 429) {
        setErrorMessage(contactContent.errorMessages.rateLimited);
        setStatus("error");
        return;
      }
      setErrorMessage(contactContent.errorMessages.server);
      setStatus("error");
    } catch {
      setErrorMessage(contactContent.errorMessages.server);
      setStatus("error");
    }
  };

  return (
    <form className="flex flex-col gap-6" noValidate onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="contact-name">{contactContent.formLabels.name}</Label>
        <Input
          aria-describedby={fieldErrors.name ? "contact-name-error" : undefined}
          aria-invalid={Boolean(fieldErrors.name)}
          className="h-11"
          id="contact-name"
          onChange={(event) => setName(event.target.value)}
          value={name}
        />
        {fieldErrors.name && (
          <p className="text-destructive text-sm" id="contact-name-error">
            {fieldErrors.name}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="contact-email">{contactContent.formLabels.email}</Label>
        <Input
          aria-describedby={
            fieldErrors.email ? "contact-email-error" : undefined
          }
          aria-invalid={Boolean(fieldErrors.email)}
          className="h-11"
          id="contact-email"
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          value={email}
        />
        {fieldErrors.email && (
          <p className="text-destructive text-sm" id="contact-email-error">
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="contact-message">
          {contactContent.formLabels.message}
        </Label>
        <Textarea
          aria-describedby={
            fieldErrors.message ? "contact-message-error" : undefined
          }
          aria-invalid={Boolean(fieldErrors.message)}
          className="min-h-[140px]"
          id="contact-message"
          onChange={(event) => setMessage(event.target.value)}
          value={message}
        />
        {fieldErrors.message && (
          <p className="text-destructive text-sm" id="contact-message-error">
            {fieldErrors.message}
          </p>
        )}
      </div>

      {/* Honeypot — real users never see or reach this field. Off-screen
          clip, not display:none (some bots specifically skip that). */}
      <div
        aria-hidden="true"
        className="absolute h-px w-px overflow-hidden"
        style={{ left: "-9999px" }}
      >
        <input
          autoComplete="off"
          id="contact-website"
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          value={website}
        />
      </div>

      {errorMessage && (
        <p className="text-destructive text-sm" role="alert">
          {errorMessage}
        </p>
      )}

      <Button disabled={status === "submitting"} size="lg" type="submit">
        {status === "submitting"
          ? contactContent.formLabels.submitting
          : contactContent.formLabels.submit}
      </Button>
    </form>
  );
}
